import { useEffect, useState } from 'react';
import TileCard from '../TileCard';
import { getCachedResponse, getE2eResults } from '../../api/client';
import type { Env, E2eResults } from '../../types';

const HELP =
  'Every check from the latest end-to-end run, in execution order. The validation ' +
  'phase sends one deliberately-invalid request per rule and expects a 400 with the ' +
  'documented message; the lifecycle phase walks a contact + case through create, ' +
  'find, update, dedup, form/case and delete. Red rows are failures.';

function formatLatency(ms: number): string {
  if (!ms || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

interface Props {
  env: Env;
  refreshToken?: number;
}

export default function E2eResultsTableTile({ env, refreshToken = 0 }: Props) {
  const [data, setData] = useState<E2eResults | null>(() =>
    getCachedResponse<E2eResults>('e2e-results', { env }),
  );
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<string | null>(null);
  const [failuresOnly, setFailuresOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedResponse<E2eResults>('e2e-results', { env });
    if (cached) setData(cached);
    setLoading(cached === null);
    setError(null);
    getE2eResults(env)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, refreshToken]);

  const allRows = data?.rows ?? [];
  const rows = failuresOnly ? allRows.filter((r) => !r.passed) : allRows;

  const footer = data?.status === 'ok' ? (
    <span className="tile-footer-row">
      <span>
        {data.runId ? `Run ${data.runId} · ` : ''}
        {data.passed ?? 0}/{data.total ?? 0} passed
        {data.baseUrl ? ` · ${data.baseUrl}` : ''}
      </span>
      <label className="tile-toggle">
        <input
          type="checkbox"
          checked={failuresOnly}
          onChange={(e) => setFailuresOnly(e.target.checked)}
        />{' '}
        Failures only
      </label>
    </span>
  ) : undefined;

  return (
    <TileCard
      title={`E2E checks — latest run (${env.toUpperCase()})`}
      help={HELP}
      loading={loading}
      error={error}
      footer={footer}
    >
      {data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}
      {data?.status === 'ok' && rows.length === 0 && (
        <p className="tile-empty">
          {failuresOnly ? 'No failures in the latest run.' : 'No checks recorded in the latest run.'}
        </p>
      )}
      {data?.status === 'ok' && rows.length > 0 && (
        <div className="workflow-table-wrap">
          <table className="workflow-table">
            <thead>
              <tr>
                <th>Phase</th>
                <th>Check</th>
                <th>Op</th>
                <th className="num">Status</th>
                <th className="num">Latency</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.phase}/${r.name}`}>
                  <td>{r.phase}</td>
                  <td>{r.name}</td>
                  <td>{r.op}</td>
                  <td className="num">{r.actualStatus || '—'}</td>
                  <td className="num">{formatLatency(r.latencyMs)}</td>
                  <td>
                    <span className={`pill pill-${r.passed ? 'succeeded' : 'failed'}`}>
                      {r.passed ? 'PASS' : 'FAIL'}
                    </span>
                    {!r.passed && r.detail ? <span className="muted"> {r.detail}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TileCard>
  );
}
